import React, { useState, useEffect } from "react";
import cx from "clsx";
import { Transition } from "react-transition-group";

import TourProgress from "@/components/tour/progress";
import LoadingSpinner from "@/components/shared/loadingSpinner";
import BldgE1Img from "@/images/tour/e1-light.svg";
import BldgE2Img from "@/images/tour/e2-light.svg";
import BldgE3Img from "@/images/tour/e3-light.svg";
import BldgE4Img from "@/images/tour/e4-light.svg";
import HandSvg from "@/images/tour/hand.svg";
import tourData from "@/data/tour-details.json";

import vars from "@/styles/_variables.scss";
import tourStyles from "@/components/tour/tour.module.scss";
import globalStyles from "@/styles/global.module.scss";
import styles from "./mobileTourBottom.module.scss";

const ICONS = {
  "bldg-e1": BldgE1Img,
  "bldg-e2": BldgE2Img,
  "bldg-e3": BldgE3Img,
  "bldg-e4": BldgE4Img
};

const renderProgress = props => {
  const { percent, stopIndex } = props;

  return (
    <TourProgress percent={percent} stopIndex={stopIndex} isDesktop={false} />
  );
};

const renderLoading = props => {
  const { tourReady, stopIndex } = props;

  return (
    <div className={cx(styles.section, styles.loadingContainer)}>
      <LoadingSpinner
        visible={!tourReady}
        isStopped={stopIndex > -1 && stopIndex < 6 ? false : true}
        className={tourStyles.spinner}
      />
    </div>
  );
};

const renderIntro = props => {
  const { tourReady, stopIndex } = props;

  if (!tourReady) return null;

  const visible = tourReady && (stopIndex < 2 || stopIndex > 9);
  const label =
    stopIndex > 6
      ? "SWIPE UP TO CONTINUE"
      : "SWIPE TO INITIATE A FLY-THROUGH OF ELCO YARDS";

  return (
    <Transition enter exit in={visible} timeout={0}>
      {state => (
        <div
          className={cx(
            styles.section,
            styles.introContainer,
            stopIndex > 6 && styles.paddingBottom,
            ["entering", "entered"].includes(state) && styles.delay,
            globalStyles.opacityAnimation,
            globalStyles[state]
          )}
        >
          <div
            className={cx(
              tourStyles.handContainer,
              tourStyles.handAnimation,
              tourStyles.entered
            )}
          >
            <HandSvg alt="Hand" />
          </div>
          <div className={styles.introText}>{label}</div>
        </div>
      )}
    </Transition>
  );
};

const renderStop = (index, props) => {
  const { stopIndex } = props;

  const data = tourData[index || 0];
  const Icon = data && data.icon ? ICONS[data.icon] : null;

  return (
    <Transition enter exit in={index === stopIndex} timeout={0}>
      {state => (
        <div
          className={cx(
            styles.section,
            styles.stopSection,
            globalStyles.opacityAnimation,
            globalStyles[state]
          )}
        >
          <div className={styles.bldgTitleRow}>
            {data ? <Icon className={styles.bldgImg} alt={data.title} /> : null}
            {data ? <div className={styles.bldgTitle}>{data.title}</div> : null}
          </div>

          <div className={cx(styles.dash, styles.large)} />
          <div className={styles.sqftTitle}>{data.sqft}</div>
          <div className={styles.introText}>OFFICE</div>
        </div>
      )}
    </Transition>
  );
};

const renderContinueScrolling = props => {
  const { stopIndex } = props;

  const visible = stopIndex > 3 && stopIndex < 9 && stopIndex % 1 !== 0;

  return (
    <Transition enter exit in={visible} timeout={0}>
      {state => (
        <div
          className={cx(
            styles.section,
            styles.continueSection,
            visible && styles.delay,
            globalStyles.opacityAnimation,
            globalStyles[state]
          )}
        >
          <div className={styles.continueText}>CONTINUE SCROLLING</div>
        </div>
      )}
    </Transition>
  );
};

const MobileTourBottom = props => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    if (!hasMounted) setHasMounted(true);
  }, []); // eslint-disable-line

  const height = hasMounted ? parseInt(vars.tourBottomHeight) : 0;

  return (
    <div className={styles.container} style={{ height }}>
      {renderProgress(props)}
      {renderLoading(props)}
      {renderIntro(props)}
      {renderStop(6, props)}
      {renderStop(7, props)}
      {renderStop(8, props)}
      {renderStop(9, props)}
      {renderContinueScrolling(props)}
    </div>
  );
};

export default MobileTourBottom;
